import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextInput } from '@vw-marketing/us-components';
import { AutoCompleteInputProps } from './types';
import {
  AutoCompleteContainer,
  DropdownList,
  CustomDropdownOption,
  InputWrapper,
  ClearButton,
} from './styles';
import { Icon } from '../Icon';
import { highlightText } from './helpers';
import { cleanPlaintext } from '../Guide/Content/Search/helpers';

export const AutoCompleteInput = ({
  defaultValue,
  value,
  options = [],
  onChange,
  onDebounceChange,
  onSelect,
  placeholder = 'Type to search...',
  customStyle,
  syncValue = true,
  label = undefined,
  inputId,
  required,
  hideLabel,
  autoFilter = false,
  debounceDelay = 300,
}: AutoCompleteInputProps) => {
  const [inputValue, setInputValue] = useState(
    defaultValue ?? value?.label ?? ''
  );
  const [showOptions, setShowOptions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const filteredOptions =
    options && autoFilter
      ? options.filter(
          (opt) =>
            opt.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            opt.value.toLowerCase().includes(inputValue.toLowerCase())
        )
      : options; // Trust parent's pre-filtered options

  const handleChange = (e) => {
    const newVal = e.target.value;
    setInputValue(newVal);
    setShowOptions(true);
    onChange?.(newVal);

    if (onDebounceChange) {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

      debounceTimeoutRef.current = setTimeout(() => {
        onDebounceChange(newVal);
      }, debounceDelay);
    }
  };

  const handleSelect = (option: { label: string; value: string }) => {
    setInputValue(cleanPlaintext(option.label));
    setShowOptions(false);
    onSelect?.(option, true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      if (inputValue.trim()) {
        const searchTermOption = {
          label: inputValue.trim(),
          value: inputValue.trim(),
        };

        setInputValue(searchTermOption.label);
        setShowOptions(false);
        onSelect?.(searchTermOption);
      }
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setInputValue('');
    setShowOptions(false);
    onChange?.('');
    onDebounceChange?.('');
    onSelect?.(null);

    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (syncValue) setInputValue(value?.label ?? '');
  }, [value, syncValue]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Set id on input and htmlFor on label for accessibility
  useEffect(() => {
    if (inputRef.current && inputId) {
      inputRef.current.id = inputId;
      const labelElement = inputRef.current.closest('label');
      if (labelElement) {
        labelElement.setAttribute('for', inputId);
      }
    }
  }, [inputId]);

  const hasValue = inputValue.trim().length > 0;

  const highlightedOptions = useMemo(() => {
    return filteredOptions.map((opt) => ({
      ...opt,
      highlightedLabel: highlightText(opt.label, inputValue),
    }));
  }, [filteredOptions, inputValue]);

  return (
    <AutoCompleteContainer ref={containerRef} style={customStyle}>
      <InputWrapper $hideLabel={hideLabel}>
        <TextInput
          ref={inputRef}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowOptions(true)}
          placeholder={placeholder}
          label={label}
          required={required && Boolean(label)}
          icon={
            <Icon
              name='Magnifier'
              style={(theme) => ({
                paddingBottom: '0px',
                color: theme.colors.content.active,
              })}
            />
          }
        />
        {hasValue && (
          <ClearButton onClick={handleClear} aria-label='Clear input'>
            <Icon
              name='CloseCircle'
              ignoreDefaultColor={true}
              style={(theme) => ({
                paddingBottom: '0px',
                color: theme.colors.content.active,
                cursor: 'pointer',
              })}
            />
          </ClearButton>
        )}
      </InputWrapper>

      {showOptions && filteredOptions.length > 0 && (
        <DropdownList>
          {highlightedOptions.map((opt) => (
            <CustomDropdownOption
              key={opt.value}
              onClick={() => handleSelect(opt)}
            >
              {opt.highlightedLabel}
            </CustomDropdownOption>
          ))}
        </DropdownList>
      )}
    </AutoCompleteContainer>
  );
};
